<script setup lang="ts">
import type { LibraryFile } from '@/modules/Library/libraryStore'
import { filterFilesByMimeTypes } from '@/modules/Project/useSupportedMimeTypes'
import FilePickerDialog from '@/modules/Workspaces/KnowledgeHub/Files/FilePickerDialog.vue'
import {
  useGoogleDriveConnection,
  type GoogleDriveFile,
} from '@/modules/Workspaces/KnowledgeHub/Integrations/GoogleDrive/useGoogleDriveConnection'
import SharePointPickerDialog from '@/modules/Workspaces/KnowledgeHub/Integrations/SharePoint/SharePointPickerDialog.vue'
import type { SharePointFile } from '@/modules/Workspaces/KnowledgeHub/Integrations/SharePoint/types'
import { useSharePointConnection } from '@/modules/Workspaces/KnowledgeHub/Integrations/SharePoint/useSharePointConnection'
import { useSharePointPicker } from '@/modules/Workspaces/KnowledgeHub/Integrations/SharePoint/useSharePointPicker'
import { getFilesFromClipboardEvent } from '@/shared/clipboard'
import { toast } from '@/shared/toast'
import { isValidUrl } from '@/shared/utils/string'
import { invariant } from '@/shared/utils/typeAssertions'
import Menu from '@/uiKit/Menu'
import { whenever } from '@vueuse/core'
import type * as zagMenu from '@zag-js/menu'
import { computed, nextTick, ref, useId, useTemplateRef } from 'vue'

/**
 * This component provides a dropdown menu for importing files from a range
 * of sources, supporting local uploads, knowledgehub files, and files
 * from integrations.
 */

const props = withDefaults(
  defineProps<{
    libraryPickerConfirmLabel: string
    positioning?: zagMenu.Props['positioning']
    /**
     * If true, the library picker will not be shown as an option.
     * Main use case for this is adding files to a Hub.
     * The library itself may become a special hub soon.
     */
    noLibrary?: boolean
    directory?: boolean
    multiple?: boolean
    supportedMimeTypes: string[] | null
  }>(),
  {
    fileInputProps: undefined,
    positioning: undefined,
    noLibrary: false,
    directory: false,
    multiple: false,
  },
)

const emit = defineEmits<{
  'import:computer': [File[]]
  'import:library': [LibraryFile[]]
  'import:google': [GoogleDriveFile[]]
  'import:sharepoint': [SharePointFile[]]
  'import:url': [string]
  'menu:close': []
  'done:library': []
}>()

const {
  connection: driveConnection,
  pick: pickDrive,
  isPickerActive: isDrivePickerActive,
} = useGoogleDriveConnection()

const { connection: sharepointConnection } = useSharePointConnection()

const {
  status: sharepointPickerStatus,
  open: openSharePointPicker,
  close: closeSharePointPicker,
} = useSharePointPicker()

async function onSharePointFiles(files: SharePointFile[]) {
  emit('import:sharepoint', files)
  closeSharePointPicker()
}

const isPickerActive = computed(
  () => isDrivePickerActive.value || sharepointPickerStatus.value === 'open',
)

const isFilePickerOpen = ref(false)
const directoryInputField = ref(false)

whenever(
  () => !isFilePickerOpen.value,
  () => emit('done:library'),
)

async function onLibrarySelect(files: LibraryFile[] = []) {
  isFilePickerOpen.value = false
  emit('import:library', files)
}

async function onDriveSelect() {
  const files = await pickDrive()
  emit('import:google', files)
}

function onOpenStateChange(open: boolean) {
  if (!open) {
    emit('menu:close')
  }
}

const fileInput = useTemplateRef('file-input')
const onAddFile = (e: Event) => {
  invariant(e.target instanceof HTMLInputElement)
  if (!e.target.files) return
  const allFiles = Array.from(e.target.files)
  const supportedTypes = props.supportedMimeTypes
  const validFiles = supportedTypes
    ? allFiles.filter((f) => supportedTypes.includes(f.type))
    : allFiles

  emit('import:computer', validFiles)
}

const fileInputId = useId()

async function onImportDirectory() {
  directoryInputField.value = true
  await nextTick()
  fileInput.value?.click()
  directoryInputField.value = false
}

const menuRef = useTemplateRef('menu')
const onPaste = (e: ClipboardEvent) => {
  const files = getFilesFromClipboardEvent(e)
  if (files) {
    const { supportedFiles, unsupportedMimeTypes } = filterFilesByMimeTypes(
      files,
      props.supportedMimeTypes || 'all',
    )
    emit('import:computer', supportedFiles)
    if (unsupportedMimeTypes.length > 0) {
      toast.error(`Unsupported file types: ${unsupportedMimeTypes.join(', ')}`)
    }
    e.stopImmediatePropagation()
    e.preventDefault()
    menuRef.value?.menu.setOpen(false)
  } else {
    // see if the clipboard contains a URL
    const clipboardText = e.clipboardData?.getData('text/plain') || ''
    const isUrl = isValidUrl(clipboardText)
    if (isUrl) {
      emit('import:url', clipboardText)
      e.stopImmediatePropagation()
      e.preventDefault()
    }
  }
}
</script>

<template>
  <Menu.Root
    ref="menu"
    v-slot="{ getTriggerProps, menu }"
    :positioning="positioning"
    close-on-select
    @change:open="onOpenStateChange"
  >
    <slot
      :get-trigger-props="
        () => {
          const props = getTriggerProps()
          return { ...props, disabled: props.disabled || isPickerActive }
        }
      "
      :menu="menu"
    />

    <Menu.Content
      class="min-w-[180px]"
      @paste="onPaste"
    >
      <Menu.Item
        :label="directory ? 'Upload files' : 'Import from computer'"
        @select="fileInput?.click()"
      />
      <Menu.Item
        v-if="directory"
        label="Upload directory"
        @select="onImportDirectory"
      />
      <Menu.Divider v-if="!noLibrary && (driveConnection || sharepointConnection)" />
      <Menu.Item
        v-if="!noLibrary"
        icon="folder"
        label="Pick from Library"
        @select="isFilePickerOpen = true"
      />
      <Menu.Item
        v-if="driveConnection"
        icon="google-drive-logo"
        label="Google Drive"
        @select="onDriveSelect"
      />
      <Menu.Item
        v-if="sharepointConnection"
        icon="sharepoint-logo"
        label="Microsoft SharePoint"
        @select="openSharePointPicker"
      />
    </Menu.Content>
  </Menu.Root>
  <FilePickerDialog
    v-model:open="isFilePickerOpen"
    only="file"
    :confirm-label="libraryPickerConfirmLabel"
    @select="onLibrarySelect"
  />
  <input
    :id="fileInputId"
    ref="file-input"
    name="file-input"
    class="hidden"
    data-test="file-upload-menu-file-input"
    type="file"
    v-bind="{
      accept: supportedMimeTypes?.join(', '),
      multiple,
    }"
    multiple
    :webkitdirectory="directoryInputField"
    @change="onAddFile"
  />
  <SharePointPickerDialog
    v-if="sharepointPickerStatus !== 'closed' && sharepointConnection"
    :connection-id="sharepointConnection.id"
    :open="sharepointPickerStatus === 'open'"
    :max-items="multiple ? 10 : 1"
    @files="onSharePointFiles"
    @close="closeSharePointPicker"
  />
</template>
